import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SparklesIcon, CheckIcon, FileTextIcon, SendIcon, SaveIcon, CalendarClockIcon, BookmarkPlusIcon, StarIcon, AlertTriangleIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { AudienceSelector } from './notice/AudienceSelector';
import { ChannelEditor } from './notice/ChannelEditor';
import { NoticePreview, type PreviewTab } from './notice/NoticePreview';
import { SendScheduler } from './notice/SendScheduler';
import { VariablesBlock } from './notice/VariablesBlock';
import { AtnaBrief } from './notice/AtnaBrief';
import { AiReviewPanel } from './notice/AiReviewPanel';
import { SaveTemplateDialog } from './notice/SaveTemplateDialog';
import type { AtnaDraft } from '../utils/atna';
import { stripHtml } from './RichTextEditor';
import { auditNotice, generateForChannel, isChannelEmpty, type AiContext, type AuditFinding } from '../utils/aiAssist';
import { getIncompleteVariables } from '../utils/variables';
import { SYSTEM_VARIABLES, type NoticeVariable } from '../types/variables';
import { CATEGORIES, SECTORS, getCategoryLabel, getTemplateCategory, type NoticeTemplate } from '../data/communications';
import { EMPTY_AUDIENCE, EMPTY_CHANNELS, EMPTY_SCHEDULE, type AudienceState, type ChannelsState, type ScheduleState } from '../types/composer';
import { describeAudience, getAudienceTotal } from '../utils/audience';
import type { Notice } from '../types/notice';

/** Converts template plain text into the HTML the rich editors expect. */
function textToHtml(text: string, imageUrl?: string): string {
  const paragraphs = text.split('\n\n').map(para => `<p>${para.replace(/\n/g, '<br>')}</p>`).join('');
  const image = imageUrl ? `<img src="${imageUrl}" alt="" style="max-width:100%;height:auto;border-radius:8px;margin:8px 0;" />` : '';
  return `${image}${paragraphs}`;
}

/**
 * Custom (per-notice) variables are temporarily hidden. System variables
 * (citizen first/last name, today's date) stay insertable via { }.
 */
const SHOW_CUSTOM_VARIABLES = false;
interface NoticeComposerProps {
  legacyMode: boolean;
  onToggleLegacy: (value: boolean) => void;
  /** Language the notice content is being written in — never the interface. */
  contentLang: 'fr' | 'en';
  /** Switches which language version of the content is being edited. */
  onContentLangChange: (value: 'fr' | 'en') => void;
  /** Notice being edited, or null when composing a new one. */
  editingNotice?: Notice | null;
  /** Every template, built-in or saved by the team, ids prefixed by category. */
  templates: NoticeTemplate[];
  /** Creates a template, or replaces `replacesId` when editing one. */
  onSaveTemplate: (template: NoticeTemplate, replacesId?: string) => void;
  onSubmit: (mode: 'draft' | 'send') => void;
  /** Composes a reusable template instead of a notice — no audience, no sending. */
  templateMode?: boolean;
  /** Template being edited in template mode, or null when creating one. */
  editingTemplate?: NoticeTemplate | null;
}
export function NoticeComposer({
  legacyMode,
  onToggleLegacy,
  contentLang,
  onContentLangChange,
  editingNotice = null,
  templates,
  onSaveTemplate,
  onSubmit,
  templateMode = false,
  editingTemplate = null
}: NoticeComposerProps) {
  const {
    t,
    lang
  } = useLanguage();
  const [category, setCategory] = useState(editingNotice?.category ?? '');
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [title, setTitle] = useState(editingNotice?.title ?? '');
  const [description, setDescription] = useState('');
  /**
   * The notice is multilingual: each language keeps its own content, while the
   * set of active channels stays shared between the two versions.
   */
  const [channelsByLang, setChannelsByLang] = useState<Record<'fr' | 'en', ChannelsState>>({
    fr: EMPTY_CHANNELS,
    en: EMPTY_CHANNELS
  });
  const channels = channelsByLang[contentLang];

  /** Writes to the language version currently being edited. */
  const setChannels = (updater: ChannelsState | ((prev: ChannelsState) => ChannelsState)) => setChannelsByLang(prev => ({
    ...prev,
    [contentLang]: typeof updater === 'function' ? (updater as (p: ChannelsState) => ChannelsState)(prev[contentLang]) : updater
  }));
  const [audience, setAudience] = useState<AudienceState>(EMPTY_AUDIENCE);
  const [schedule, setSchedule] = useState<ScheduleState>(EMPTY_SCHEDULE);
  /** Variables live only for this notice — never saved to the configuration. */
  const [variables, setVariables] = useState<NoticeVariable[]>(SYSTEM_VARIABLES);
  const [previewTab, setPreviewTab] = useState<PreviewTab>('push');
  const [confirmation, setConfirmation] = useState<string | null>(null);
  /** Template metadata — only edited in template mode. */
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  // Seed the form from the template being edited.
  useEffect(() => {
    if (!templateMode || !editingTemplate) return;
    setCategory(getTemplateCategory(editingTemplate));
    setTemplateName(editingTemplate.name[lang]);
    setTemplateDescription(editingTemplate.description[lang]);
    applyTemplate(editingTemplate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateMode, editingTemplate]);

  // Seed the form from the notice being edited.
  useEffect(() => {
    if (!editingNotice) return;
    setTitle(editingNotice.title);
    setCategory(editingNotice.category);
    const seeded = {
      ...EMPTY_CHANNELS
    };
    (Object.keys(seeded) as (keyof ChannelsState)[]).forEach(key => {
      seeded[key] = {
        ...seeded[key],
        active: editingNotice.channels.includes(key as never)
      } as never;
    });
    setChannelsByLang({
      fr: seeded,
      en: seeded
    });
    const editedSectors = editingNotice.audience.kind === 'sector' ? SECTORS.filter(s => editingNotice.audience.detail?.fr.includes(s.label.fr.replace('Secteurs ', ''))).map(s => s.id) : [];
    setAudience(() => ({
      ...EMPTY_AUDIENCE,
      scope: editedSectors.length > 0 ? 'places' : 'all',
      places: editedSectors.map(sectorId => ({
        id: `pl-${sectorId}`,
        kind: 'sector' as const,
        sectorId
      }))
    }));
    setSchedule({
      ...EMPTY_SCHEDULE,
      mode: editingNotice.status === 'scheduled' ? 'scheduled' : 'now',
      date: editingNotice.status === 'scheduled' ? editingNotice.date.slice(0, 10) : '',
      time: editingNotice.status === 'scheduled' ? editingNotice.date.slice(11, 16) : '09:00'
    });
    const firstChannel = editingNotice.channels[0];
    if (firstChannel && firstChannel !== 'social') {
      setPreviewTab(firstChannel as PreviewTab);
    }
  }, [editingNotice]);
  const audienceTotal = useMemo(() => getAudienceTotal(audience), [audience]);
  const audienceLabel = describeAudience(audience, lang);

  /** Everything the assistant reasons about, rebuilt as the notice changes. */
  const aiContext: AiContext = useMemo(() => ({
    title,
    category,
    categoryLabel: category ? getCategoryLabel(category)[lang] : '',
    audienceTotal,
    audienceLabel,
    channels,
    variables,
    contentLang
  }), [title, category, lang, audienceTotal, audienceLabel, channels, variables, contentLang]);
  const otherLang: 'fr' | 'en' = contentLang === 'fr' ? 'en' : 'fr';

  /** Active channels that already hold content, per language version. */
  const filledIn = (state: ChannelsState) => (Object.keys(state) as (keyof ChannelsState)[]).filter(key => state[key].active && !isChannelEmpty(key, state));
  const langStatus = useMemo(() => ({
    fr: filledIn(channelsByLang.fr).length > 0,
    en: filledIn(channelsByLang.en).length > 0
  }), [channelsByLang]);

  /** One version is written and the other is still untouched. */
  const canGenerateOtherLang = langStatus[contentLang] && !langStatus[otherLang];

  /**
   * Writes the missing language version from the one already completed, then
   * switches the editor over to it so it can be reviewed.
   */
  const generateOtherLang = () => {
    const source = channelsByLang[contentLang];
    const sourceCtx: AiContext = {
      ...aiContext,
      contentLang: otherLang
    };
    setChannelsByLang(prev => {
      const target = {
        ...prev[otherLang]
      };
      filledIn(source).forEach(key => {
        target[key] = {
          ...target[key],
          active: true,
          ...(generateForChannel(key, sourceCtx) as object)
        } as never;
      });
      return {
        ...prev,
        [otherLang]: target
      };
    });
    onContentLangChange(otherLang);
    setConfirmation(otherLang === 'en' ? t('Version anglaise générée', 'English version generated') : t('Version française générée', 'French version generated'));
    window.setTimeout(() => setConfirmation(null), 4000);
  };
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [findings, setFindings] = useState<AuditFinding[]>([]);

  /** Fills the whole form from what ATNA wrote, then jumps to its main channel. */
  const applyAtnaDraft = (draft: AtnaDraft) => {
    setTitle(draft.title);
    setCategory(draft.category);
    setSelectedTemplate(null);
    setChannels(draft.channels);
    setAudience(prev => ({
      ...prev,
      ...draft.audience
    }));
    const firstChannel = (Object.keys(draft.channels) as (keyof typeof draft.channels)[]).find(key => draft.channels[key].active && key !== 'social');
    if (firstChannel) setPreviewTab(firstChannel as PreviewTab);
  };
  const runReview = () => {
    setReviewOpen(true);
    setReviewLoading(true);
    window.setTimeout(() => {
      setFindings(auditNotice(aiContext));
      setReviewLoading(false);
    }, 900);
  };
  const availableTemplates = useMemo(() => {
    if (!category) return [];
    // Team templates first, then the built-in catalogue.
    return templates.filter(tpl => getTemplateCategory(tpl) === category).sort((a, b) => Number(Boolean(b.custom)) - Number(Boolean(a.custom)));
  }, [category, templates]);
  const patchAudience = (patch: Partial<AudienceState>) => setAudience(prev => ({
    ...prev,
    ...patch
  }));
  const patchSchedule = (patch: Partial<ScheduleState>) => setSchedule(prev => ({
    ...prev,
    ...patch
  }));

  /** Channel selection belongs to the notice, so it applies to both versions. */
  const toggleChannel = (key: keyof ChannelsState) => {
    const willActivate = !channels[key].active;
    setChannelsByLang(prev => ({
      fr: {
        ...prev.fr,
        [key]: {
          ...prev.fr[key],
          active: willActivate
        }
      },
      en: {
        ...prev.en,
        [key]: {
          ...prev.en[key],
          active: willActivate
        }
      }
    }));
    if (willActivate) setPreviewTab(key as PreviewTab);
  };
  const updateChannel = <K extends keyof ChannelsState,>(key: K, patch: Partial<ChannelsState[K]>) => setChannels(prev => ({
    ...prev,
    [key]: {
      ...prev[key],
      ...patch
    }
  }));
  const applyTemplate = (tpl: NoticeTemplate) => {
    setSelectedTemplate(tpl.id);
    setTitle(tpl.title);
    setDescription(tpl.push.message);
    setChannels(prev => ({
      ...prev,
      email: {
        active: true,
        subject: tpl.email.subject,
        body: textToHtml(tpl.email.body, tpl.imageUrl),
        attachments: []
      },
      sms: {
        active: true,
        text: tpl.sms
      },
      push: {
        active: true,
        title: tpl.push.title,
        message: tpl.push.message
      },
      social: tpl.social ? {
        active: true,
        postText: textToHtml(tpl.social, tpl.imageUrl)
      } : prev.social
    }));
    setPreviewTab('push');
  };
  const hasAnyContent = Boolean(title.trim()) || langStatus.fr || langStatus.en;

  /** Variables used in the content but left without a value for this language. */
  const incompleteVariables = useMemo(() => getIncompleteVariables(variables, channels, contentLang), [variables, channels, contentLang]);
  const canSubmit = templateMode ? Boolean(category) && Boolean(templateName.trim()) && hasAnyContent : incompleteVariables.length === 0 && (schedule.mode === 'scheduled' ? Boolean(schedule.date) && hasAnyContent : hasAnyContent);
  const submitLabel = templateMode ? editingTemplate ? t('Enregistrer les modifications', 'Save changes') : t('Créer le modèle', 'Create template') : schedule.mode === 'scheduled' ? t("Programmer l'envoi", 'Schedule sending') : editingNotice ? t('Enregistrer et envoyer', 'Save and send') : t('Envoyer maintenant', 'Send now');
  const SubmitIcon = templateMode ? BookmarkPlusIcon : schedule.mode === 'scheduled' ? CalendarClockIcon : SendIcon;

  /** Snapshot of the current content as a reusable template. */
  const buildTemplate = (id: string, name: string, templateDesc: string): NoticeTemplate => ({
    id,
    name: {
      fr: name,
      en: name
    },
    description: {
      fr: templateDesc || 'Modèle enregistré par votre équipe',
      en: templateDesc || 'Template saved by your team'
    },
    title,
    email: {
      subject: channels.email.subject,
      body: stripHtml(channels.email.body)
    },
    sms: stripHtml(channels.sms.text),
    push: {
      title: stripHtml(channels.push.title),
      message: stripHtml(channels.push.message)
    },
    social: stripHtml(channels.social.postText) || undefined,
    custom: true
  });
  const closeSaveDialog = useCallback(() => setSaveDialogOpen(false), []);

  /** "Save as template" from the notice composer — the notice stays open. */
  const saveNoticeAsTemplate = (name: string, templateDesc: string) => {
    onSaveTemplate(buildTemplate(`${category}:${Date.now()}`, name, templateDesc));
    setSaveDialogOpen(false);
    setConfirmation(t(`Modèle « ${name} » enregistré`, `Template “${name}” saved`));
    window.setTimeout(() => setConfirmation(null), 4000);
  };
  const handleSubmit = () => {
    if (!canSubmit) return;
    if (templateMode) {
      // Keep the template's identity when editing, even if its category moves.
      const suffix = editingTemplate ? editingTemplate.id.split(':').slice(1).join(':') : String(Date.now());
      const saved = buildTemplate(`${category}:${suffix}`, templateName.trim(), templateDescription.trim());
      onSaveTemplate(saved, editingTemplate?.id);
      onSubmit('draft');
      return;
    }
    onSubmit('send');
  };
  return <div className="font-['Inter'] bg-[#F7F7F8] pb-28" data-id="element-4258" data-mp="ip6ykxqxxi92">
      <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8 items-start" data-id="element-4259" data-mp="innmzkg8bvdm">
        {/* LEFT — FORM */}
        <div className="space-y-10 min-w-0" data-id="element-4260" data-mp="66uuoe02yvqa">
          {/* 1. ATNA BRIEF */}
          {!templateMode && <AtnaBrief channels={channels} contentLang={contentLang} onGenerate={applyAtnaDraft} data-id="element-4261" data-mp="17i35fy9l0hd" />}

          {/* 2. GENERAL INFORMATION */}
          <section className="space-y-6" data-id="element-4262" data-mp="3oczkyw0s1am5">
            <div className="flex items-center justify-between gap-4" data-id="element-4263" data-mp="3odsgt1y76fpi">
              <h2 className="text-xl font-semibold text-slate-900" data-id="element-4264" data-mp="3jkpw7jbeeglg">
                {templateMode ? t('Informations du modèle', 'Template information') : t('Informations générales', 'General information')}
              </h2>
              <label className={`flex items-center gap-2 cursor-pointer select-none ${templateMode ? 'hidden' : ''}`} data-id="element-4265" data-mp="35lzyc1gr8cw1">
                <span className="text-xs font-medium text-slate-400" data-id="element-4266" data-mp="30p99aomz26yh">
                  {t('Version classique', 'Classic version')}
                </span>
                <button type="button" role="switch" aria-checked={legacyMode} aria-label={t('Version classique', 'Classic version')} onClick={() => onToggleLegacy(!legacyMode)} className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#4F46E5] focus:ring-offset-1 ${legacyMode ? 'bg-[#4F46E5]' : 'bg-slate-200'}`} data-id="element-4267" data-mp="2v1xphx7ftel9">
                  <span aria-hidden="true" className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${legacyMode ? 'translate-x-4' : 'translate-x-0'}`} data-id="element-4268" data-mp="34x7osd6gb7vc" />
                </button>
              </label>
            </div>

            <div className="bg-white rounded-xl border-[0.5px] border-slate-200 p-6 space-y-6" data-id="element-4269" data-mp="1h0rpcitcup5e">
              {templateMode && <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pb-6 border-b border-slate-100" data-id="element-4270" data-mp="1qr2wsfc8zgh1">
                  <div data-id="element-4271" data-mp="131ielynx4n8t">
                    <label htmlFor="template-name" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4272" data-mp="130qgag2xsbeu">
                      {t('Nom du modèle', 'Template name')}
                    </label>
                    <input id="template-name" type="text" value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder={t('Ex. : Fermeture de rue', 'E.g. Street closure')} className="block w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] transition-colors" data-id="element-4273" data-mp="1bzu1pqpkt2wi" />
                  </div>
                  <div data-id="element-4274" data-mp="3bk9t27u5clj4">
                    <label htmlFor="template-description" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4275" data-mp="3bjs13neioth0">
                      {t('Description', 'Description')}{' '}
                      <span className="font-normal text-slate-400" data-id="element-4276" data-mp="3q9g20nisdq47">
                        {t('(facultatif)', '(optional)')}
                      </span>
                    </label>
                    <input id="template-description" type="text" value={templateDescription} onChange={e => setTemplateDescription(e.target.value)} placeholder={t('Quand l’utiliser', 'When to use it')} className="block w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] transition-colors" data-id="element-4277" data-mp="359l8zic49mu" />
                  </div>
                </div>}
              <div data-id="element-4278" data-mp="1jlp8620rachm">
                <label htmlFor="notice-title" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4279" data-mp="1jm8lt6jdiepu">
                  {templateMode ? t('Titre de l’avis généré', 'Title of the generated notice') : t('Titre de l’avis', 'Notice title')}
                </label>
                <input id="notice-title" type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder={t('Ex. : Avis d’ébullition d’eau préventif', 'E.g. Preventive boil water advisory')} className="block w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] transition-colors" data-id="element-4280" data-mp="27de37q4ypf29" />
              </div>

              {/* Category grid */}
              <div data-id="element-4281" data-mp="39n2kodfxsw2l">
                <span className="block text-sm font-medium text-slate-700 mb-2" data-id="element-4282" data-mp="39nls307yo22d">
                  {t('Catégorie', 'Category')}
                </span>
                <div role="radiogroup" aria-label={t('Catégorie', 'Category')} className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2" data-id="element-4283" data-mp="a52wkyg91u3f">
                  {CATEGORIES.map(c => {
                  const Icon = c.icon;
                  const active = category === c.value;
                  return <button key={c.value} type="button" role="radio" aria-checked={active} onClick={() => {
                    setCategory(c.value);
                    setSelectedTemplate(null);
                  }} className={`flex flex-col items-center justify-center gap-2 px-2 py-3.5 rounded-xl border text-center transition-colors ${active ? 'border-[#4F46E5] bg-indigo-50/50 ring-1 ring-[#4F46E5]' : 'border-slate-200 bg-white hover:border-[#4F46E5] hover:bg-slate-50'}`} data-id="element-4284" data-mp="2luvi2wjmbsuq">
                        <span className={`inline-flex w-9 h-9 rounded-lg items-center justify-center ${active ? 'bg-[#4F46E5] text-white' : c.urgent ? 'bg-red-50 text-red-500' : 'bg-slate-100 text-slate-500'}`} data-id="element-4285" data-mp="2zj5cm7ye8mz3">
                          <Icon className="w-4 h-4" data-id="element-4286" data-mp="x3wt24oty7pe" />
                        </span>
                        <span className={`text-xs font-medium leading-tight ${active ? 'text-[#4F46E5]' : 'text-slate-600'}`} data-id="element-4287" data-mp="x69i11t09iby">
                          {c.label[lang]}
                        </span>
                      </button>;
                })}
                </div>
              </div>

              {/* Template suggestions */}
              <AnimatePresence data-id="element-4288" data-mp="1uqc9d35iszbd">
                {category && !templateMode && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-4289" data-mp="1psaqai83gzzw">
                    <div className="pt-1" data-id="element-4290" data-mp="2iftvuw1x921s">
                      <div className="flex items-center gap-2 mb-3" data-id="element-4291" data-mp="2nenef2w2fj8t">
                        <SparklesIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-4292" data-mp="2nbrghek7wukd" />
                        <span className="block text-sm font-medium text-slate-700" data-id="element-4293" data-mp="29dujwi5rko5p">
                          {t('Modèles suggérés', 'Suggested templates')}
                        </span>
                      </div>
                      {availableTemplates.length > 0 ? <div className="space-y-2" data-id="element-4294" data-mp="24gb1r75y3r8h">
                          {availableTemplates.map(tpl => {
                      const active = selectedTemplate === tpl.id;
                      return <button key={tpl.id} type="button" onClick={() => applyTemplate(tpl)} className={`w-full flex items-center gap-3 text-left p-3 rounded-lg border transition-colors ${active ? 'border-[#4F46E5] bg-indigo-50/40' : 'border-slate-200 hover:border-[#4F46E5] hover:bg-slate-50'}`} data-id="element-4295" data-mp="1uppvqynxzkfj">
                                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${active ? 'bg-[#4F46E5] text-white' : 'bg-slate-100 text-slate-500'}`} data-id="element-4296" data-mp="2aaj6izbv62oi">
                                  {active ? <CheckIcon className="w-4 h-4" data-id="element-4297" data-mp="3qpayjb53l7xa" /> : tpl.custom ? <StarIcon className="w-4 h-4" data-id="element-4298" data-mp="3lr9gmtjerene" /> : <FileTextIcon className="w-4 h-4" data-id="element-4299" data-mp="3t8xuattv5od0" />}
                                </div>
                                <div className="min-w-0 flex-1" data-id="element-4300" data-mp="6tw9x8sfcglf">
                                  <p className="text-sm font-semibold text-slate-900 truncate flex items-center gap-2" data-id="element-4301" data-mp="6uo72o1o6mdt">
                                    {tpl.name[lang]}
                                    {tpl.custom && <span className="px-1.5 py-0.5 rounded bg-indigo-50 text-[#4F46E5] text-[10px] font-bold uppercase tracking-wide" data-id="element-4302" data-mp="3f9ogg0t4r6c7">
                                        {t('Modèle', 'Template')}
                                      </span>}
                                  </p>
                                  <p className="text-xs text-slate-500 truncate" data-id="element-4303" data-mp="zhifgml7pvax">
                                    {tpl.description[lang]}
                                  </p>
                                </div>
                              </button>;
                    })}
                          <p className="text-xs text-slate-400 pt-1" data-id="element-4304" data-mp="2vkwx9qxa4kr6">
                            {t('Cliquez sur un modèle pour remplir automatiquement le contenu des canaux.', 'Click a template to automatically fill in the channel content.')}
                          </p>
                        </div> : <p className="text-sm text-slate-400 py-2" data-id="element-4305" data-mp="2cx1ludp53j18">
                          {t('Aucun modèle disponible pour cette catégorie. Rédigez votre contenu dans les canaux ci-dessous.', 'No template available for this category. Write your content in the channels below.')}
                        </p>}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </section>

          {/* 3. AUDIENCE & 4. VARIABLES — notice-only */}
          {!templateMode && <>
              <AudienceSelector audience={audience} onChange={patchAudience} audienceTotal={audienceTotal} data-id="element-4306" data-mp="3vuxxs37zuhwp" />

              {/* Custom variables temporarily hidden — flip to re-enable. */}
              {SHOW_CUSTOM_VARIABLES && <VariablesBlock variables={variables} onChange={setVariables} channels={channels} contentLang={contentLang} data-id="element-4307" data-mp="wdq784lgrnqg" />}
            </>}

          {/* 5. CHANNELS */}
          <ChannelEditor channels={channels} onToggle={toggleChannel} onUpdate={updateChannel} audienceTotal={audienceTotal} variables={variables} contentLang={contentLang} onContentLangChange={onContentLangChange} langStatus={langStatus} canGenerateOtherLang={canGenerateOtherLang} onGenerateOtherLang={generateOtherLang} aiContext={aiContext} data-id="element-4308" data-mp="334o8cwb7vpvj" />

          {/* 6. SENDING TIME */}
          {!templateMode && <SendScheduler schedule={schedule} onChange={patchSchedule} categoryLabel={category ? getCategoryLabel(category)[lang] : null} data-id="element-4309" data-mp="3kwpfq4ugwm7e" />}
        </div>

        {/* RIGHT — LIVE PREVIEW */}
        <div className="lg:sticky lg:top-8" data-id="element-4310" data-mp="cm4blhij9rn3">
          <NoticePreview channels={channels} title={title} description={description} categoryLabel={category ? getCategoryLabel(category)[lang] : t('Général', 'General')} previewTab={previewTab} onPreviewTabChange={setPreviewTab} variables={variables} contentLang={contentLang} data-id="element-4311" data-mp="ckt71fh9kfux" />
        </div>
      </div>

      {/* ACTION BAR */}
      <div className="sticky bottom-0 mt-10 -mb-28 bg-white/95 backdrop-blur border-t border-slate-200 z-30" data-id="element-4312" data-mp="1cnr9gjqhjzyk">
        <div className="max-w-6xl mx-auto px-1 py-3 flex flex-wrap items-center gap-3 justify-between" data-id="element-4313" data-mp="1hkhqpae111lk">
          <div className="min-w-0" role="status" aria-live="polite" data-id="element-4314" data-mp="1mmgu2k9rnyii">
            {incompleteVariables.length > 0 && <span className="inline-flex items-center gap-2 text-sm font-medium text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-lg" data-id="element-4315" data-mp="1rifkseikhsm3">
                <AlertTriangleIcon className="w-4 h-4 flex-shrink-0" data-id="element-4316" data-mp="20dvb4lk45pmx" />
                {incompleteVariables.length}{' '}
                {t('variable(s) sans valeur : ', 'variable(s) without a value: ')}
                <span className="font-semibold" data-id="element-4317" data-mp="2acb4kv6m5azl">
                  {incompleteVariables.map(v => v.label[lang]).join(', ')}
                </span>
              </span>}
            {confirmation && incompleteVariables.length === 0 && <span className="inline-flex items-center gap-2 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-lg" data-id="element-4318" data-mp="3m0ai6lgfo435">
                <CheckIcon className="w-4 h-4" data-id="element-4319" data-mp="382lyhgy3r2zo" />
                {confirmation}
              </span>}
          </div>

          <div className="flex flex-wrap items-center gap-3" data-id="element-4320" data-mp="3mkufpwacht9">
            {templateMode ? <button type="button" onClick={() => onSubmit('draft')} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-4321" data-mp="8lnvkz95a3vy">
                {t('Annuler', 'Cancel')}
              </button> : <>
                <button type="button" onClick={runReview} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-[#4F46E5] bg-white border border-[#4F46E5] rounded-lg hover:bg-indigo-50 transition-colors" data-id="element-4322" data-mp="rbwykbcr9zop">
                  <SparklesIcon className="w-4 h-4" data-id="element-4323" data-mp="2ls3ybb9yjcvw" />
                  {t("Vérifier l'avis", 'Review the notice')}
                </button>

                <button type="button" onClick={() => setSaveDialogOpen(true)} disabled={!hasAnyContent} title={hasAnyContent ? undefined : t('Rédigez du contenu d’abord', 'Write some content first')} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-4324" data-mp="2gtsrl9eylihw">
                  <BookmarkPlusIcon className="w-4 h-4" data-id="element-4325" data-mp="39hon4assl4d3" />
                  {t('Enregistrer comme modèle', 'Save as template')}
                </button>

                <button type="button" onClick={() => onSubmit('draft')} className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-4326" data-mp="3ljpu6i4lo3dh">
                  <SaveIcon className="w-4 h-4" data-id="element-4327" data-mp="2xvpuutt834na" />
                  {t('Enregistrer le brouillon', 'Save draft')}
                </button>
              </>}

            <button type="button" onClick={handleSubmit} disabled={!canSubmit} className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors shadow-sm disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-[#4F46E5]" data-id="element-4328" data-mp="37t4a4q3etne0">
              <SubmitIcon className="w-4 h-4" data-id="element-4329" data-mp="1185hik7w4b4i" />
              {submitLabel}
            </button>
          </div>
        </div>
      </div>

      <AiReviewPanel open={reviewOpen} loading={reviewLoading} findings={findings} onClose={() => setReviewOpen(false)} data-id="element-4330" data-mp="x6af1ce83rp5" />

      <SaveTemplateDialog open={saveDialogOpen} categoryLabel={category ? getCategoryLabel(category)[lang] : null} defaultName={title} onClose={closeSaveDialog} onSave={saveNoticeAsTemplate} data-id="element-4331" data-mp="n8cv313ech2c" />
    </div>;
}